import React, { useState, useEffect, useCallback } from 'react';
import { X, ArrowRight, CheckCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = 'fpm_exit_intent_shown';
const STORAGE_CLAIMED = 'fpm_discount_claimed';

export default function ExitIntentPopup() {
  const [visible, setVisible] = useState(false);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [claimed, setClaimed] = useState(false);
  const [error, setError] = useState('');
  const [hasShown, setHasShown] = useState(false);

  const showPopup = useCallback(() => {
    const alreadyShown = localStorage.getItem(STORAGE_KEY);
    const alreadyClaimed = localStorage.getItem(STORAGE_CLAIMED);
    if (!alreadyShown && !alreadyClaimed && !hasShown) {
      setVisible(true);
      setHasShown(true);
      localStorage.setItem(STORAGE_KEY, 'true');
    }
  }, [hasShown]);

  useEffect(() => {
    // Exit intent: mouse leaves top of viewport
    const handleMouseLeave = (e) => {
      if (e.clientY <= 5) showPopup();
    };

    // Also show after 45 seconds of inactivity
    const timer = setTimeout(showPopup, 45000);

    document.addEventListener('mouseleave', handleMouseLeave);
    return () => {
      document.removeEventListener('mouseleave', handleMouseLeave);
      clearTimeout(timer);
    };
  }, [showPopup]);

  const handleClose = () => {
    setVisible(false);
  };

  const handleDecline = () => {
    setVisible(false);
    localStorage.setItem(STORAGE_KEY, 'declined');
  };

  const handleClaim = async (e) => {
    e.preventDefault();
    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      setError('Please enter a valid email address');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await fetch(`${BACKEND_URL}/api/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, source: 'exit_intent' }),
      });
      setClaimed(true);
      localStorage.setItem(STORAGE_CLAIMED, 'true');
      // Store the discount code in session
      sessionStorage.setItem('fpm_promo_code', 'FIRSTORDER');
    } catch {
      // Even if API fails, show success (graceful)
      setClaimed(true);
      localStorage.setItem(STORAGE_CLAIMED, 'true');
    } finally {
      setLoading(false);
    }
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative z-10 bg-white rounded-2xl overflow-hidden shadow-2xl max-w-3xl w-full flex flex-col md:flex-row max-h-[95vh]">
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-3 right-3 z-20 w-8 h-8 rounded-full bg-white/90 shadow flex items-center justify-center text-gray-600 hover:text-gray-900 transition-colors"
          aria-label="Close"
        >
          <X size={16} />
        </button>

        {/* Left: Food imagery */}
        <div className="hidden md:block md:w-5/12 relative shrink-0">
          <img
            src="https://images.unsplash.com/photo-1543352632-5a4b24e4d2a6?fm=jpg&q=80&w=600&auto=format&fit=crop"
            alt="Fresh meal prep"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-transparent to-black/20" />
          {/* Floating meal images */}
          <div className="absolute bottom-6 left-4 right-4 grid grid-cols-2 gap-2">
            <img
              src="https://images.unsplash.com/photo-1636401870585-a8852371e84a?fm=jpg&q=60&w=200&auto=format&fit=crop"
              className="rounded-lg h-20 w-full object-cover shadow-lg border-2 border-white"
              alt="Orange chicken"
            />
            <img
              src="https://images.unsplash.com/photo-1762631383588-7c9df7e91e64?fm=jpg&q=60&w=200&auto=format&fit=crop"
              className="rounded-lg h-20 w-full object-cover shadow-lg border-2 border-white"
              alt="Lemon pepper chicken"
            />
          </div>
        </div>

        {/* Right: Offer form */}
        <div className="flex-1 p-8 flex flex-col items-center justify-center text-center overflow-y-auto">
          {/* Logo */}
          <img
            src="https://img1.wsimg.com/isteam/ip/cba6336a-37c0-45fc-ab61-0529e3165a67/IMG_9044-39480c5.jpeg/:/rs=h:80,cg:true,m/qt=q:95"
            alt="Fit Pro Meals"
            className="h-12 w-auto object-contain mb-5"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />

          {claimed ? (
            <div className="space-y-4">
              <CheckCircle size={52} className="text-green-500 mx-auto" />
              <h2 className="text-2xl font-extrabold text-gray-900">You're In! 🎉</h2>
              <p className="text-gray-500 text-base">
                Use code <span className="font-bold text-green-600 bg-green-50 px-2 py-0.5 rounded">FIRSTORDER</span> at checkout for your first order discount.
              </p>
              <p className="text-gray-400 text-xs">Check your email for details and this week's menu.</p>
              <button
                onClick={() => setVisible(false)}
                className="w-full bg-green-600 text-white py-3.5 rounded-xl font-bold hover:bg-green-700 transition-colors"
              >
                View This Week's Menu →
              </button>
            </div>
          ) : (
            <>
              <p className="text-gray-500 text-sm font-medium uppercase tracking-widest mb-2">
                Don't leave without this
              </p>
              <h2 className="text-4xl font-extrabold text-gray-900 mb-1">
                $10 Off
              </h2>
              <p className="text-green-600 font-bold text-lg mb-1">
                Your First Order
              </p>
              <p className="text-gray-400 text-sm mb-6 max-w-xs">
                Enter your email and get an exclusive $10 discount on your first meal prep delivery.
              </p>

              <form onSubmit={handleClaim} className="w-full space-y-3">
                <div className="relative">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setError(''); }}
                    placeholder="Email address"
                    className="w-full border border-gray-200 rounded-xl px-4 py-3.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 transition"
                  />
                  {error && <p className="text-red-500 text-xs mt-1 text-left">{error}</p>}
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gray-900 text-white py-3.5 rounded-xl font-bold text-sm hover:bg-gray-800 disabled:opacity-60 transition-colors flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>Claim My $10 Off <ArrowRight size={16} /></>
                  )}
                </button>

                <button
                  type="button"
                  onClick={handleClose}
                  className="w-full bg-gray-100 text-gray-700 py-3.5 rounded-xl font-semibold text-sm hover:bg-gray-200 transition-colors"
                >
                  Continue Without Offer
                </button>
              </form>

              <button
                onClick={handleDecline}
                className="mt-4 text-xs text-gray-400 underline hover:text-gray-600 transition-colors"
              >
                No thanks, I don't want a discount
              </button>

              <p className="mt-4 text-[10px] text-gray-400 max-w-xs leading-relaxed">
                By providing your email, you agree to receive marketing emails from Fit Pro Meals. Unsubscribe anytime.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
